import React, { ChangeEvent, useState } from 'react';
import {
	Stack,
	Typography,
	MenuItem,
	Grid,
	InputLabel,
	FormControl,
	useMediaQuery,
	useTheme,
	FormControlLabel,
	InputAdornment,
	Tooltip,
	IconButton,
} from '@mui/material';
import { DpButtonForm } from '@/components/blocks/DpLibrary/DpButtonForm';
import {
	containerDpValeFormSX,
	fieldsDpValeFormSX,
	titleDpValeFormSX,
} from '@/components/blocks/DpLibrary/DpDpValeForm/style/main';
import { styled } from '@mui/material/styles';
import { useNotifications } from '@/data/Content/Notifications';
import ClearIcon from '@mui/icons-material/Clear';
import { useSingleSettings } from '@/data/Settings';
import { AccountSelectSX } from '@/components/content/Account/styles/accountPersonal';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { BpCheckbox } from '@/components/content/Account/parts/InformationForm';
import { Linkable } from '@/components/blocks/Linkable';
import ErrorIcon from '@mui/icons-material/Error';
import { DpOutlinedSelect } from '@/components/blocks/DpOutlineSelect';
import { AccountDropdownIcon } from '@/components/content/Account/parts/icons/AccountDropdownIcon';
import {
	DPVALE_FORM_CLIENT_ID,
	DPVALE_FORM_DE_EXTERNAL_KEY,
	DPVALE_FORM_ACTION,
	DPVALE_FORM_RETURN_XML,
	DPVALE_FORM_SUCCESS_URL,
	DPVALE_FORM_ERROR_URL,
	DPVALE_FORM_URL,
} from '@/data/config/DPVALE_FORM';

const HelperText = styled(Typography)<{
	showError: boolean;
}>(({ theme, showError }) => ({
	color: showError ? theme.palette.error.main : theme.palette.grey[400],
	fontSize: 'var(--font-size-caption)',
	mt: 0.2,
	marginTop: '8px',
}));

interface AddressInfoInter {
	city: string;
	state: string;
	neighborhood: string[];
}

const initialAnswers: { [key: string]: string } = {};

const initialAddressInfo: AddressInfoInter = {
	city: '',
	state: '',
	neighborhood: [],
};

export const DpDpValeForm = ({
	title,
	formfields = [],
	handleCloseModal,
}: {
	title?: string;
	formfields?: any;
	handleSubmit?: () => void;
	handleCloseModal?: () => void;
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [answers, setAnswers] = useState<{ [key: string]: string }>(initialAnswers);
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const [addressInfo, setAddressInfo] = useState(initialAddressInfo);
	const [aceptarTerminos, setAceptarTerminos] = useState(false);
	const { settings } = useSingleSettings();
	const apiKey = settings?.userData['wc.StoreLocator.GoogleMaps_api'];

	const CustomIconComponent = (props: any) => <AccountDropdownIcon {...props} />;

	const { showSuccessMessage, showSnackbarMessage } = useNotifications();

	const validateName = (name: string) => {
		const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/;
		return regex.test(name);
	};

	const validatePhone = (phone: string) => {
		const regex = /^[0-9]{0,10}$/;
		return regex.test(phone);
	};

	const validateEmail = (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const handleSelectChange = (name: string, event: any) => {
		const value = event.target.value as string;
		setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
		setAnswers((prevAnswers) => ({
			...prevAnswers,
			[name]: value,
		}));
	};
	const handleZipCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
		const zipCode = event.target.value || '';

		if (/^\d*$/.test(zipCode) && zipCode.length <= 5) {
			handleInputChange('zipCode', event);

			if (!zipCode) {
				handleInputChange('state', {
					target: {
						name: 'state',
						value: '',
					},
				} as ChangeEvent<HTMLInputElement>);
				handleInputChange('city', {
					target: {
						name: 'city',
						value: '',
					},
				} as ChangeEvent<HTMLInputElement>);
				setAddressInfo({
					city: '',
					state: '',
					neighborhood: [],
				});
				return;
			}

			if (zipCode.length === 5) {
				fetchAddressInfo(zipCode);
				setErrors((prev) => ({
					...prev,
					zipCode: '',
				}));
				return;
			}
			if (zipCode.length < 5) {
				fetchAddressInfo(zipCode);
				setErrors((prev) => ({
					...prev,
					zipCode: '',
				}));
				return;
			}
		}
	};
	const fetchAddressInfo = async (zipCode: string) => {
		try {
			const response = await fetch(
				`https://maps.googleapis.com/maps/api/geocode/json?address=${zipCode}&components=country:MX&key=${apiKey}`
			);
			const data = await response.json();

			if (data.results.length > 0 && data.status === 'OK') {
				const addressComponents = data.results[0].address_components;
				const addressLocalities = data.results[0].postcode_localities;

				const city =
					addressComponents.find((component: any) => component.types.includes('locality'))
						?.long_name ||
					addressComponents.find((component: any) => component.types.includes('political'))
						?.long_name ||
					'';

				const state =
					addressComponents.find((component: any) =>
						component.types.includes('administrative_area_level_1')
					)?.long_name || '';
				setAddressInfo({ city, state, neighborhood: addressLocalities });
				if (!state) {
					handleInputChange('state', {
						target: {
							name: 'state',
							value: '',
						},
					} as ChangeEvent<HTMLInputElement>);
					handleInputChange('city', {
						target: {
							name: 'city',
							value: '',
						},
					} as ChangeEvent<HTMLInputElement>);

					setAddressInfo({
						city: '',
						state: '',
						neighborhood: [],
					});

					setErrors((prev) => ({
						...prev,
						zipCode: 'Código postal invalido',
					}));
					return;
				}
				handleInputChange('state', {
					target: {
						name: 'state',
						value: state,
					},
				} as ChangeEvent<HTMLInputElement>);
				handleInputChange('city', {
					target: {
						name: 'city',
						value: city,
					},
				} as ChangeEvent<HTMLInputElement>);

				setErrors((prev) => ({
					...prev,
					zipCode: '',
				}));
			}
		} catch (error) {
			console.error('Error fetching address information:', error);
		}
	};

	const handleInputChange = (name: string, event: any) => {
		const value = event.target.value;

		if (name === 'firstname' || name === 'lastname') {
			if (validateName(value)) {
				setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
				setAnswers((prevAnswers) => ({
					...prevAnswers,
					[name]: value,
				}));
			}
		} else if (name === 'phone') {
			if (validatePhone(value)) {
				setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
				setAnswers((prevAnswers) => ({
					...prevAnswers,
					[name]: value.slice(0, 10),
				}));
			}
		} else if (name === 'numero') {
			if (validatePhone(value)) {
				setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
				setAnswers((prevAnswers) => ({
					...prevAnswers,
					[name]: value.slice(0, 10),
				}));
			}
		} else {
			setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
			setAnswers((prevAnswers) => ({
				...prevAnswers,
				[name]: value,
			}));
		}
	};

	const handleBlur = (name: string) => {
		if (name === 'email') {
			const emailValue = answers[name] || '';
			if (validateEmail(emailValue)) {
				setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
			} else {
				setErrors((prevErrors) => ({ ...prevErrors, [name]: 'Correo electrónico no válido' }));
			}
		} else if (name === 'phone') {
			if (answers[name] && answers[name].length < 10) {
				setErrors((prevErrors) => ({
					...prevErrors,
					[name]: 'Solo se permiten hasta 10 dígitos.',
				}));
			}
		} else {
			if (!answers[name]) {
				setErrors((prevErrors) => ({ ...prevErrors, [name]: 'Este campo es requerido' }));
			}
		}
	};

	const isFormComplete = () => {
		const requiredFields = [
			'firstname',
			'lastname',
			'email',
			'phone',
			'calle',
			'colony',
			'city',
			'state',
			'experience',
			'contact',
			'hour',
		];
		return requiredFields.every((field) => !!answers[field]);
	};

	const onMessageError = (error?: string) => {
		showSnackbarMessage(
			error || 'Ocurrió un error al enviar el formulario. Por favor, inténtelo de nuevo.'
		);
	};

	const handleSubmitClick = () => {
		const newErrors: { [key: string]: string } = {};

		formfields.forEach((field: any) => {
			if (field.name === 'phone') {
				if (answers.phone) {
					if (answers.phone.length < 10) {
						newErrors[field.name] = 'Solo se permiten hasta 10 dígitos.';
					}
				}
			}
			if (field && !answers[field.name]) {
				newErrors[field.name] = 'Este campo es requerido';
			}
		});

		if (!aceptarTerminos) {
			newErrors.aceptarTerminos = 'Debes aceptar el aviso de privacidad.';
		}

		setErrors(newErrors);

		if (Object.keys(newErrors).length > 0 || !isFormComplete()) {
			return;
		}

		const {
			calle,
			city,
			contact,
			email,
			experience,
			firstname,
			hour,
			lastname,
			numero,
			phone,
			state,
			colony,
		} = answers;

		const addressline = `${calle}, ${numero}`;

		const newAnwers = {
			Ciudad: city,
			'Medio de contacto': contact,
			Email: email,
			Experiencia: experience,
			Nombres: firstname,
			Apellidos: lastname,
			Telefono: phone,
			'Hora de contacto': hour,
			Domicilio: addressline,
			Estado: state,
			Colonia: colony,
		};

		const body = new URLSearchParams({
			_clientID: DPVALE_FORM_CLIENT_ID,
			_deExternalKey: DPVALE_FORM_DE_EXTERNAL_KEY,
			_action: DPVALE_FORM_ACTION,
			_returnXML: DPVALE_FORM_RETURN_XML,
			_successURL: DPVALE_FORM_SUCCESS_URL,
			_errorURL: DPVALE_FORM_ERROR_URL,
			...newAnwers,
		}).toString();

		fetch(DPVALE_FORM_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body,
		})
			.then((response) => {
				if (response.ok) {
					showSuccessMessage('Tu formulario ha sido enviado con éxito.');
					return;
				}
				onMessageError();
			})
			.catch(() => {
				onMessageError();
			})
			.finally(() => {
				setAnswers(initialAnswers);
				setAddressInfo(initialAddressInfo);
				setAceptarTerminos(false);
			});
	};

	const isTablet = useMediaQuery(theme.breakpoints.down('md'));

	return (
		<Stack sx={{ height: '100%', position: 'relative' }}>
			<Stack sx={containerDpValeFormSX}>
				{title ? (
					<Typography component={'h2'} sx={titleDpValeFormSX}>
						{title}
					</Typography>
				) : null}
				{isMobile || isTablet ? (
					<ClearIcon
						sx={{
							position: 'absolute',
							left: { sm: '94%', xs: '90%' },
							top: { sm: '1%', xs: '2%' },
							fontSize: 'var(--font-size-xxl)',
						}}
						onClick={handleCloseModal}
					></ClearIcon>
				) : null}
				<Grid container spacing={1}>
					{Array.isArray(formfields)
						? // eslint-disable-next-line complexity
						  formfields.map((field: any, idx: number) => (
								<Grid sx={fieldsDpValeFormSX} xs={12} md={field.name === 'hour' ? 12 : 6} key={idx}>
									<Stack spacing={1}>
										{field.type === 'selectCol' ? (
											<FormControl
												fullWidth
												required
												disabled={
													!(addressInfo.neighborhood && addressInfo.neighborhood.length > 0)
												}
												variant="outlined"
												sx={{ borderRadius: '5.5px' }}
											>
												<InputLabel
													sx={{
														backgroundColor: '#FFF',
														padding: '0px',
														lineHeight: 1,
														height: '20px',
														fontSize: 'var(--font-size-body)',
													}}
													id="address-form-address-line2-label"
												>
													{field.label}
												</InputLabel>
												<DpOutlinedSelect
													fullWidth
													value={answers[field.name] || ''}
													onChange={(event) => handleSelectChange(field.name, event)}
													onBlur={() => handleBlur(field.name)}
													disabled={!!!addressInfo.neighborhood.length}
													sx={{
														...AccountSelectSX,
														'& .MuiOutlinedInput-notchedOutline': {
															borderColor: errors[field.name] ? '#CE1616' : '#b5beca',
														},
														'&:hover .MuiOutlinedInput-notchedOutline': {
															borderColor: errors[field.name] ? '#CE1616' : '#b5beca',
														},
														'& .MuiSelect-select': {
															color: 'inherit',
														},
													}}
												>
													{addressInfo.neighborhood?.map((nh) => (
														<MenuItem key={nh} value={nh}>
															{nh}
														</MenuItem>
													))}
												</DpOutlinedSelect>
												{errors[field.name] ? (
													<HelperText sx={{ marginTop: '0px' }} showError={true}>
														{errors[field.name]}
													</HelperText>
												) : null}
											</FormControl>
										) : field.type === 'select' ? (
											<FormControl
												fullWidth
												required
												variant="outlined"
												sx={{ borderRadius: '5.5px' }}
											>
												<InputLabel
													sx={{
														backgroundColor: '#FFF',
														padding: '0px',
														color: 'inherit',
														lineHeight: 1,
														height: '20px',
														fontSize: 'var(--font-size-body)',
													}}
													id="address-form-address-line2-label"
												>
													{field.label}
												</InputLabel>
												<DpOutlinedSelect
													fullWidth
													value={answers[field.name] || ''}
													onChange={(event) => handleSelectChange(field.name, event)}
													IconComponent={CustomIconComponent}
													onBlur={() => handleBlur(field.name)}
													sx={{
														...AccountSelectSX,
														'& .MuiOutlinedInput-notchedOutline': {
															borderColor: errors[field.name] ? '#CE1616' : '#b5beca',
														},
														'&:hover .MuiOutlinedInput-notchedOutline': {
															borderColor: errors[field.name] ? '#CE1616' : '#b5beca',
														},
														'& .MuiSelect-select': {
															color: 'inherit',
														},
													}}
												>
													{field.options?.map((option: string, idx: number) => (
														<MenuItem key={idx} value={option}>
															{option}
														</MenuItem>
													))}
												</DpOutlinedSelect>
												{errors[field.name] ? (
													<HelperText sx={{ marginTop: '0px' }} showError={true}>
														{errors[field.name]}
													</HelperText>
												) : null}
											</FormControl>
										) : field.type === 'textCp' ? (
											<DpOutlinedInput
												label={field.label}
												value={answers[field.name] || ''}
												onChange={handleZipCodeChange}
												required={field.required}
												onBlur={() => handleBlur(field.name)}
												error={!!errors[field.name]}
												helperText={errors[field.name]}
												FormHelperTextProps={{
													style: { padding: 0, margin: 0 },
												}}
												fullWidth
											/>
										) : (
											<>
												<DpOutlinedInput
													label={field.label}
													value={answers[field.name] || ''}
													onChange={(event) => handleInputChange(field.name, event)}
													required={field.required}
													onBlur={() => handleBlur(field.name)}
													error={
														field.name === 'state' || field.name === 'city'
															? false
															: !!errors[field.name]
													}
													FormHelperTextProps={{
														style: { padding: 0, margin: 0 },
													}}
													helperText={
														field.name === 'state' || field.name === 'city'
															? ''
															: errors[field.name]
													}
													disabled={field.name === 'state' || field.name === 'city'}
													InputProps={{
														endAdornment: field.name === 'email' && (
															<InputAdornment sx={{ zIndex: 99 }} position="end">
																<Tooltip
																	title="ⓘ En el correo solo usar letras minúsculas, sin espacios."
																	arrow
																	open={tooltipOpen}
																	onClose={() => setTooltipOpen(false)}
																	onOpen={() => setTooltipOpen(true)}
																	disableInteractive
																>
																	<IconButton
																		onClick={() => setTooltipOpen((prev) => !prev)}
																		sx={{
																			width: '10px',
																			height: '10px',
																			color: 'rgb(201, 201, 201)',
																		}}
																	>
																		<ErrorIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
															</InputAdornment>
														),
													}}
												/>
												{field.name === 'email' ? (
													<Typography
														sx={{
															fontSize: 'var(--font-size-small)',
															color: '#707070',
															letterSpacing: '0px',
															paddingTop: '3px',
															marginTop: '0px !important',
															whiteSpace: 'nowrap',
														}}
													>
														{'ⓘ Enviaremos un código de verificación a este número'}
													</Typography>
												) : null}
											</>
										)}
									</Stack>
								</Grid>
						  ))
						: null}
				</Grid>

				<Grid item xs={12}>
					<FormControlLabel
						control={
							<BpCheckbox
								checked={aceptarTerminos}
								name="aceptarTerminos"
								onChange={(e) => setAceptarTerminos(e.target.checked)}
							/>
						}
						label={
							<Typography sx={{ fontSize: 'var(--font-size-default)' }}>
								{'He leído y consentido el '}
								<Linkable
									href="https://dpcard.mx/aviso-privacidad"
									target="_blank"
									sx={{ color: '#0467D2', fontSize: 'var(--font-size-default)' }}
								>
									{'Aviso de privacidad'}
								</Linkable>
							</Typography>
						}
					/>
					{errors.aceptarTerminos ? (
						<Typography
							sx={{ color: '#d32f2f', fontSize: '0.70rem', marginTop: '-5px', marginBottom: '8px' }}
						>
							{errors.aceptarTerminos}
						</Typography>
					) : null}
				</Grid>
				<DpButtonForm label="Enviar" onClick={handleSubmitClick} />
			</Stack>
		</Stack>
	);
};
